import { useCallback, useRef, useState } from "react";

import {
  apiClient,
  type CreateExportPayload,
  type ExportRow,
  type SnapshotFacetFilters
} from "@/shared/api/api-client";
import { ApiError, errorMessage } from "@/shared/api/http";
import { Toast } from "@/shared/components/Toast";
import { usePolling } from "@/shared/hooks/usePolling";
import { isExportActive } from "@/shared/vocabulary/exports";

/** Cadência da checagem do botão "Gerando…": mais lenta que a do
 *  upload (2,5 s) — a planilha leva segundos a minutos, e quem quer
 *  acompanhar tem a página de Downloads. */
export const EXPORT_BUTTON_POLL_MS = 5000;

type Notice = { message: string; withLink: boolean };

/**
 * Pedir uma planilha em fila e acompanhar o pedido até terminar
 * (2026-10-15). Os quatro botões de exportação (relatório, dashboard e as
 * duas telas de upload) usam o mesmo hook, e é ele quem decide:
 *
 * - **o botão fica "Gerando…"** enquanto o pedido que ESTA tela fez
 *   anda, checado por `getExport` a cada 5 s com `pausedUntil` (a regra
 *   do 429). A decisão: o hook acompanha só o último pedido feito na
 *   sessão da tela — quem recarrega a página perde o "Gerando…", e
 *   clicar de novo devolve o pedido ativo (200) em vez de gerar outro,
 *   que é a trava do servidor;
 * - **falha de ação não troca a página**: o erro vira texto abaixo do
 *   botão, e o pedido que falhou lá no worker também (a mensagem do
 *   servidor), uma vez só;
 * - **sem pop-up de confirmação**: o aviso é um `Toast`, que oferece
 *   ir a Downloads (quando a tela sabe navegar).
 */
export function useExportRequest({
  token,
  onOpenDownloads
}: {
  token: string;
  /** Leva à página de Downloads. Sem ela o aviso não oferece o link. */
  onOpenDownloads?: () => void;
}) {
  const [tracked, setTracked] = useState<ExportRow | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  // O pedido em acompanhamento, lido pelo tick sem recriá-lo.
  const trackedId = useRef<number | null>(null);

  const rememberPause = (failure: unknown) => {
    if (failure instanceof ApiError && failure.retryAfterSeconds) {
      setPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
    }
  };

  // O miolo do pedido, qualquer que seja a rota que o faz (`perform`).
  const requestWith = useCallback(
    async (perform: () => Promise<ExportRow>): Promise<boolean> => {
      setRequesting(true);
      setError(null);
      try {
        const row = await perform();
        if (!isExportActive(row.status)) {
          // O servidor devolveu uma linha que NÃO está andando (hoje, só
          // por defesa: a falha em retomada é 409). "Gerando…" de uma
          // execução que falhou é a mentira que esta checagem impede.
          setError(row.message || "Não foi possível gerar a planilha.");
          return false;
        }
        trackedId.current = row.id;
        setTracked(row);
        setNotice({
          message:
            "Gerando a planilha — você será avisada no sino; o arquivo fica em Downloads.",
          withLink: true
        });
        return true;
      } catch (failure) {
        rememberPause(failure);
        setError(errorMessage(failure, "Não foi possível pedir a planilha."));
        return false;
      } finally {
        setRequesting(false);
      }
    },
    []
  );

  const request = useCallback(
    (payload: CreateExportPayload, filters?: SnapshotFacetFilters): Promise<boolean> =>
      requestWith(() => apiClient.createExport(token, payload, filters)),
    [token, requestWith]
  );

  const refresh = useCallback(async () => {
    const id = trackedId.current;
    if (id === null) return;
    try {
      const row = await apiClient.getExport(token, id);
      setTracked(row);
      if (row.status === "FINISHED") {
        setNotice({ message: "Planilha pronta — baixe em Downloads.", withLink: true });
      } else if (row.status === "ERRORED") {
        setError(row.message || "Não foi possível gerar a planilha.");
      }
    } catch (failure) {
      // Piscada de rede ou 429 no acompanhamento não derruba nada: o
      // próximo tick tenta de novo (o 429 só empurra o próximo).
      rememberPause(failure);
    }
  }, [token]);

  // Estáveis de propósito: o `Toast` rearma o temporizador a cada nova
  // identidade de `onClose`, e um re-render do polling o seguraria na tela.
  const clearError = useCallback(() => setError(null), []);
  const dismissNotice = useCallback(() => setNotice(null), []);

  const active = tracked !== null && isExportActive(tracked.status);
  usePolling({
    enabled: active,
    onTick: () => void refresh(),
    signature: tracked?.status,
    pausedUntil,
    intervalMs: EXPORT_BUTTON_POLL_MS
  });

  return {
    request,
    /** Pede por outra rota (ex.: as menções vivas) com o mesmo
     *  acompanhamento, aviso e erro. */
    requestWith,
    /** O botão deve mostrar "Gerando…" e ficar desligado. */
    busy: requesting || active,
    error,
    clearError,
    notice,
    dismissNotice,
    onOpenDownloads
  };
}

export type ExportRequester = ReturnType<typeof useExportRequest>;

/** O que a tela mostra do pedido: a falha embaixo do botão e o aviso
 *  passageiro. Um componente só pra as quatro telas não reescreverem. */
export function ExportFeedback({
  exporter,
  suppressError = false
}: {
  exporter: ExportRequester;
  /** O diálogo de formato aberto mostra o erro por dentro; mostrá-lo
   *  também aqui o deixaria atrás do fundo escurecido. */
  suppressError?: boolean;
}) {
  const { notice, error, onOpenDownloads } = exporter;
  return (
    <>
      {error && !suppressError && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <Toast
          message={notice.message}
          actionLabel={notice.withLink && onOpenDownloads ? "Abrir Downloads" : undefined}
          onAction={
            notice.withLink && onOpenDownloads
              ? () => {
                  exporter.dismissNotice();
                  onOpenDownloads();
                }
              : undefined
          }
          onClose={exporter.dismissNotice}
          durationMs={10000}
        />
      )}
    </>
  );
}
